(() => {
  'use strict';

  const sectionSelector = '[data-cgg-collection][data-cgg-progressive="true"]';
  const maxAge = 10 * 60 * 1000;
  const cachePrefix = 'cgg-collection-grid:';

  const initialize = (root = document) => {
    const sections = root.matches?.(sectionSelector)
      ? [root]
      : root.querySelectorAll?.(sectionSelector) || [];

    sections.forEach(section => {
      if (section.dataset.cggProgressiveReady || !window.fetch || !window.DOMParser) return;
      const current = new URL(window.location.href);
      const previewTheme = window.Shopify?.theme;
      if (['unpublished', 'development', 'demo'].includes(previewTheme?.role) && previewTheme.id && !window.Shopify?.designMode) {
        current.searchParams.set('preview_theme_id', previewTheme.id);
      }
      if (['title-ascending', 'title-descending'].includes(current.searchParams.get('sort_by')) && !window.Shopify?.designMode) return;

      const grid = section.querySelector('[data-cgg-grid]');
      const controls = section.querySelector('[data-cgg-load-controls]');
      const button = section.querySelector('[data-cgg-load-more]');
      const nextLink = section.querySelector('[data-cgg-next]');
      const status = section.querySelector('[data-cgg-load-status]');
      const pagination = section.querySelector('[data-cgg-pagination]');
      const sectionId = section.dataset.sectionId;
      if (!grid || !controls || !button || !nextLink || !status || !pagination || !sectionId || !nextLink.getAttribute('href')) return;

      const normalizeNext = (raw, previous) => {
        if (!raw) return null;
        let url;
        try { url = new URL(raw, current); } catch { return null; }
        if (url.origin !== current.origin || url.pathname !== current.pathname) return null;
        for (const [key, value] of current.searchParams) {
          if (key === 'view' || key === 'preview_theme_id' || key === 'sort_by' || key.startsWith('filter.')) {
            if (key === 'preview_theme_id' || !url.searchParams.has(key)) url.searchParams.set(key, value);
          }
        }
        url.searchParams.delete('section_id');
        url.hash = '';
        const prior = new URL(previous || current.href);
        if (url.href === prior.href) return null;
        const oldPage = Number(prior.searchParams.get('page') || '1');
        const newPage = Number(url.searchParams.get('page') || '1');
        if (!Number.isInteger(oldPage) || !Number.isInteger(newPage) || newPage <= oldPage) return null;
        return url;
      };

      let next = normalizeNext(nextLink.getAttribute('href'), current.href);
      if (!next) return;
      section.dataset.cggProgressiveReady = 'true';
      const cacheKey = cachePrefix + current.href + ':' + sectionId + ':' + (window.Shopify?.theme?.id || 'unknown-theme');
      let loadedCount = 0;
      let automaticCount = 0;
      let pending = false;
      let alive = true;
      let controller = null;
      let observer = null;
      const seenUrls = new Set([current.href]);

      const productKey = card => {
        const href = card.querySelector('a[href*="/products/"]')?.getAttribute('href');
        if (!href) return null;
        try { return new URL(href, current).pathname.replace(/\/$/, ''); }
        catch { return null; }
      };
      const seenProducts = new Set([...grid.querySelectorAll('.cgg-card')].map(productKey).filter(Boolean));
      const setStatus = key => { status.textContent = status.dataset[key] || ''; };
      const save = () => {
        try {
          sessionStorage.setItem(cacheKey, JSON.stringify({
            timestamp: Date.now(), html: grid.innerHTML, next: next?.href || null,
            loadedCount, automaticCount, scrollY: window.scrollY
          }));
        } catch { /* Storage may be unavailable or full. */ }
      };
      const finish = () => {
        next = null;
        nextLink.removeAttribute('href');
        nextLink.hidden = true;
        button.hidden = true;
        observer?.disconnect();
        setStatus('complete');
      };
      const restore = () => {
        const navigation = performance.getEntriesByType?.('navigation')?.[0];
        if (navigation?.type !== 'back_forward') return;
        try {
          const saved = JSON.parse(sessionStorage.getItem(cacheKey) || 'null');
          if (!saved || Date.now() - saved.timestamp > maxAge || saved.loadedCount < 1 || typeof saved.html !== 'string') return;
          const restoredNext = saved.next ? normalizeNext(saved.next, current.href) : null;
          if (saved.next && !restoredNext) return;
          const parsed = new DOMParser().parseFromString(saved.html, 'text/html');
          const cards = [...parsed.body.children];
          if (!cards.length || cards.some(card =>
            !card.classList.contains('cgg-card') && !card.classList.contains('cgg-collection__empty'))) return;
          grid.replaceChildren(...cards.map(card => document.importNode(card, true)));
          seenProducts.clear();
          grid.querySelectorAll('.cgg-card').forEach(card => {
            const key = productKey(card);
            if (key) seenProducts.add(key);
          });
          next = restoredNext;
          loadedCount = saved.loadedCount;
          automaticCount = Math.min(2, saved.automaticCount || 0);
          if (next) nextLink.href = next.href;
          else finish();
          if (Number.isFinite(saved.scrollY)) {
            requestAnimationFrame(() => requestAnimationFrame(() => window.scrollTo(0, saved.scrollY)));
          }
        } catch { /* Corrupt or blocked storage leaves the server page intact. */ }
      };

      restore();
      controls.hidden = false;
      pagination.hidden = true;
      nextLink.hidden = true;
      if (next) nextLink.href = next.href;

      const load = async (automatic = false) => {
        if (!alive || pending || !next) return;
        pending = true;
        button.disabled = true;
        setStatus('loading');
        const requested = next;
        controller = new AbortController();
        try {
          const requestUrl = new URL(requested.href);
          requestUrl.searchParams.set('section_id', sectionId);
          const response = await fetch(requestUrl.href, {
            signal: controller.signal, credentials: 'same-origin', headers: { Accept: 'text/html' }
          });
          if (!response.ok) throw new Error('Collection request failed');
          const html = new DOMParser().parseFromString(await response.text(), 'text/html');
          const returnedSection = [...html.querySelectorAll(sectionSelector)]
            .find(candidate => candidate.dataset.sectionId === sectionId);
          const returnedGrid = returnedSection?.querySelector('[data-cgg-grid]');
          const returnedNext = returnedSection?.querySelector('[data-cgg-next]')?.getAttribute('href');
          if (!returnedGrid) throw new Error('Collection grid missing');
          const following = returnedNext ? normalizeNext(returnedNext, requested.href) : null;
          if (returnedNext && (!following || seenUrls.has(following.href))) throw new Error('Collection page did not advance');
          const cards = [...returnedGrid.children].filter(card => card.classList.contains('cgg-card'));
          const fresh = cards.filter(card => {
            const key = productKey(card);
            if (!key || seenProducts.has(key)) return false;
            seenProducts.add(key);
            return true;
          });
          if (!alive) return;
          let first = null;
          if (fresh.length) {
            first = document.importNode(fresh[0], true);
            grid.querySelector('.cgg-collection__empty')?.remove();
            grid.append(first, ...fresh.slice(1).map(card => document.importNode(card, true)));
          }
          seenUrls.add(requested.href);
          loadedCount += 1;
          if (automatic) automaticCount += 1;
          next = following;
          if (next) {
            nextLink.href = next.href;
            nextLink.hidden = true;
            setStatus('');
          } else finish();
          save();
          if (!automatic) first?.querySelector('a[href]')?.focus({ preventScroll: true });
          if (automaticCount >= 2) observer?.disconnect();
          else if (!automatic) observe();
          if (automatic && !fresh.length && next && automaticCount < 2) {
            queueMicrotask(() => { if (alive && !pending) load(true); });
          }
        } catch (error) {
          if (!alive || error.name === 'AbortError') return;
          observer?.disconnect();
          nextLink.hidden = false;
          setStatus('error');
        } finally {
          pending = false;
          controller = null;
          if (alive) button.disabled = false;
        }
      };

      const onClick = () => load(false);
      const observe = () => {
        if (!next || automaticCount >= 2 || !('IntersectionObserver' in window)) return;
        if (!observer) observer = new IntersectionObserver(entries => {
          if (entries.some(entry => entry.isIntersecting)) load(true);
        }, { rootMargin: '500px 0px' });
        observer.observe(controls);
      };
      const onPageHide = () => {
        save();
        controller?.abort();
        observer?.disconnect();
      };
      const onPageShow = event => { if (event.persisted && alive) observe(); };
      const onUnload = event => {
        if (event.target !== section && !event.target.contains(section)) return;
        alive = false;
        controller?.abort();
        observer?.disconnect();
        button.removeEventListener('click', onClick);
        window.removeEventListener('pagehide', onPageHide);
        window.removeEventListener('pageshow', onPageShow);
        document.removeEventListener('shopify:section:unload', onUnload);
      };
      button.addEventListener('click', onClick);
      window.addEventListener('pagehide', onPageHide);
      window.addEventListener('pageshow', onPageShow);
      document.addEventListener('shopify:section:unload', onUnload);
      observe();
    });
  };

  initialize();
  document.addEventListener('shopify:section:load', event => initialize(event.target));
})();
